import { useMemo, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { discordApi, streamsApi } from '../api';
import './Sidebar.css';

const menuItems = [
  { title: 'Dashboard', icon: '📊', to: '/' },
  { title: 'Live Streams', icon: '📺', to: '/streams' },
  { title: 'Top Streamer', icon: '🏆', to: '/top-streamers' },
  { title: 'Community', icon: '👥', to: '/community' },
  { title: 'Streamer Chat', icon: '💬', to: '/chat' }
];

  const streamerMenuItem = { title: 'Dein Account', icon: '💰', to: '/streamer-account' };
  const redeemedCoinsMenuItem = { title: 'Coins Übersicht', icon: '🎫', to: '/redeemed-coins' };

export default function Sidebar({ open, onClose, userData, handleLogout, isAuthenticated }) {
  const location = useLocation();
  const [widget, setWidget] = useState(null);
  const [topStreamers, setTopStreamers] = useState([]);
  const [liveCount, setLiveCount] = useState(0);
  const [hasRedeemHistoryAccess, setHasRedeemHistoryAccess] = useState(false);
  const [pendingPayoutCount, setPendingPayoutCount] = useState(0);
  const [twitchBotOpen, setTwitchBotOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      discordApi.widget()
        .then(data => { if (!cancelled) setWidget(data); })
        .catch(() => { if (!cancelled) setWidget({ enabled: false }); });
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      streamsApi.getLive()
        .then(data => {
          if (cancelled) return;
          setLiveCount(data?.length || 0);
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadTop = () => {
      streamsApi.getAll()
        .then(data => {
          if (cancelled) return;
          const map = new Map();
          for (const s of data) {
            if (!s) continue;
            const key = (s.display_name || s.username || '').toLowerCase();
            if (!map.has(key)) {
              map.set(key, { ...s, total_votes: 0 });
            }
            const entry = map.get(key);
            entry.total_votes += s.vote_count || 0;
          }
          const top = Array.from(map.values())
            .sort((a, b) => b.total_votes - a.total_votes)
            .slice(0, 3);
          setTopStreamers(top);
        })
        .catch(() => {});
    };
    loadTop();
    const t = setInterval(loadTop, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setHasRedeemHistoryAccess(false);
      setPendingPayoutCount(0);
      return;
    }

    let cancelled = false;
    const token = localStorage.getItem('discord_token');

    fetch(`${process.env.REACT_APP_API_BASE || '/api'}/user-data`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        if (!cancelled) {
          const REDEEM_HISTORY_ROLE_ID = '1496560391071862795';
          const hasAccess = Array.isArray(data.roles) && data.roles.includes(REDEEM_HISTORY_ROLE_ID);
          setHasRedeemHistoryAccess(hasAccess);

          if (hasAccess) {
            return fetch(`${process.env.REACT_APP_API_BASE || '/api'}/streamer/account/pending-count`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then(res => res.json())
              .then(countData => {
                if (!cancelled) {
                  setPendingPayoutCount(countData?.count || 0);
                }
              })
              .catch(() => {
                if (!cancelled) setPendingPayoutCount(0);
              });
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasRedeemHistoryAccess(false);
          setPendingPayoutCount(0);
        }
      });

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const menu = useMemo(() => {
    const items = isAuthenticated ? [...menuItems, streamerMenuItem] : menuItems;

    return (
      <nav className="sidebar-nav">
        {items.map((item) => {
          const isActive = location.pathname === item.to || (item.to === '/' && location.pathname === '/');
          const showLiveBadge = item.to === '/streams';
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => {
                if (window.innerWidth <= 768) onClose();
              }}
              className={() => `nav-item ${isActive ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-title">{item.title}</span>
              {showLiveBadge && <span className={`nav-badge ${liveCount > 0 ? 'nav-badge-live' : 'nav-badge-offline'}`}>{liveCount}</span>}
            </NavLink>
          );
        })}
        {isAuthenticated && hasRedeemHistoryAccess && (
          <>
            <div
              className={`nav-item nav-category ${twitchBotOpen ? 'active' : ''}`}
              onClick={() => setTwitchBotOpen(!twitchBotOpen)}
              style={{ cursor: 'pointer' }}
            >
              <span className="nav-icon">🤖</span>
              <span className="nav-title">Twitch Bot</span>
              <span className="nav-chevron">{twitchBotOpen ? '▾' : '▸'}</span>
            </div>
            {twitchBotOpen && (
              <div className="nav-submenu">
                <NavLink
                  to="/twitch-account"
                  onClick={() => {
                    if (window.innerWidth <= 768) onClose();
                  }}
                  className={() => `nav-item nav-subitem ${location.pathname === '/twitch-account' ? 'active' : ''}`}
                >
                  <span className="nav-icon">📊</span>
                  <span className="nav-title">Account Info</span>
                </NavLink>
                <NavLink
                  to="/giveaway"
                  onClick={() => {
                    if (window.innerWidth <= 768) onClose();
                  }}
                  className={() => `nav-item nav-subitem ${location.pathname === '/giveaway' ? 'active' : ''}`}
                >
                  <span className="nav-icon">🎁</span>
                  <span className="nav-title">Giveaway</span>
                </NavLink>
                <NavLink
                  to="/bot-timers"
                  onClick={() => {
                    if (window.innerWidth <= 768) onClose();
                  }}
                  className={() => `nav-item nav-subitem ${location.pathname === '/bot-timers' ? 'active' : ''}`}
                >
                  <span className="nav-icon">⏱️</span>
                  <span className="nav-title">Auto-Nachrichten</span>
                </NavLink>
                <NavLink
                  to="/bot-commands"
                  onClick={() => {
                    if (window.innerWidth <= 768) onClose();
                  }}
                  className={() => `nav-item nav-subitem ${location.pathname === '/bot-commands' ? 'active' : ''}`}
                >
                  <span className="nav-icon">⚡</span>
                  <span className="nav-title">Commands</span>
                </NavLink>
              </div>
            )}
            <NavLink
              to="/redeemed-coins"
              onClick={() => {
                if (window.innerWidth <= 768) onClose();
              }}
              className={() => `nav-item ${location.pathname === '/redeemed-coins' ? 'active' : ''}`}
            >
              <span className="nav-icon">{redeemedCoinsMenuItem.icon}</span>
              <span className="nav-title">{redeemedCoinsMenuItem.title}</span>
              {pendingPayoutCount > 0 && <span className="nav-badge">{pendingPayoutCount}</span>}
            </NavLink>
          </>
        )}
      </nav>
    );
  }, [location.pathname, onClose, isAuthenticated, hasRedeemHistoryAccess, pendingPayoutCount, liveCount, twitchBotOpen]);

  return (
    <>
      <aside className={`sidebar ${open ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h2>Salerno Streamerpanel</h2>
          <span className="version">v1.1.0</span>
        </div>
        {menu}
        <div className="sidebar-voting">
          <div className="sidebar-voting-header">
            <span>🏆 Voting</span>
            <NavLink to="/top-streamers" className="sidebar-voting-link" onClick={() => { if (window.innerWidth <= 768) onClose(); }}>
              Vote
            </NavLink>
          </div>
          <p className="sidebar-voting-slogan">Wähle deinen Lieblings-Streamer</p>
          {topStreamers.length > 0 ? (
            <div className="sidebar-voting-list">
              {topStreamers.map((s, i) => (
                <NavLink
                  key={s.id}
                  to="/top-streamers"
                  onClick={() => { if (window.innerWidth <= 768) onClose(); }}
                  className="sidebar-voting-item"
                >
                  <span className="sidebar-voting-rank">#{i + 1}</span>
                  <span className="sidebar-voting-name">{s.display_name || s.username || 'Unbekannt'}</span>
                  <p className="sidebar-voting-slogan">Gesamt: ⭐ {s.total_votes || 0}</p>
                </NavLink>
              ))}
            </div>
          ) : (
            <p className="sidebar-voting-empty">Noch keine Stimmen.</p>
          )}
        </div>
        <div className="sidebar-footer">
          <a
            href={widget?.enabled && widget.instant_invite ? widget.instant_invite : "https://discord.gg/salernocity"}
            target="_blank"
            rel="noreferrer"
            className="discord-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3.2a.075.075 0 0 0-.079.037c-.34.6-.717 1.387-.98 2.005a18.27 18.27 0 0 0-5.487 0 12.65 12.65 0 0 0-.997-2.005.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 5.18 4.369a.07.07 0 0 0-.032.027C2.34 8.61 1.65 12.74 2.05 16.81a.083.083 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.2 14.2 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.193.372-.293a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.1.245.2.372.294a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-4.7-.838-8.795-3.549-12.413a.06.06 0 0 0-.031-.028M8.02 14.331c-1.182 0-2.156-1.085-2.156-2.418 0-1.334.955-2.419 2.156-2.419 1.21 0 2.175 1.094 2.156 2.42 0 1.332-.955 2.417-2.156 2.417m7.974 0c-1.183 0-2.157-1.085-2.157-2.418 0-1.334.955-2.419 2.157-2.419 1.21 0 2.175 1.094 2.156 2.42 0 1.332-.946 2.417-2.156 2.417" />
            </svg>
            <span>Discord beitreten</span>
            {widget?.enabled && (
              <span className="discord-btn-stats" title="Online / Mitglieder im Widget">
                <span className="discord-online-dot" />
                    {widget.presence_count} online
              </span>
            )}
          </a>
          <div className="copyright">© 2026 Salerno</div>
          {isAuthenticated ? (
            <button onClick={handleLogout} className="logout-btn">
              Abmelden
            </button>
          ) : (
            <a href={`https://discord.com/api/oauth2/authorize?client_id=${process.env.REACT_APP_DISCORD_CLIENT_ID}&redirect_uri=${encodeURIComponent(window.location.origin + '/api/discord/callback')}&response_type=code&scope=identify+guilds+connections`} className="login-btn">
              Mit Discord anmelden
            </a>
          )}
        </div>
      </aside>
      {open && <div className="sidebar-overlay" onClick={onClose} />}
    </>
  );
}
